import React, { useContext, useState, useMemo, useRef } from 'react';
import shuffle from 'lodash/shuffle';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';

import styled from 'styled-components/macro';

import { fontkoKR } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';
import GTM from '@mirinae/apis/GoogleAnalytics';

import { lessonsPalette } from '@mirinae/lessons/modules/defines/styles';
import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { getFields } from '@mirinae/lessons/modules/libs/elementLib';
import { FormattedText } from '@mirinae/lessons/components/lesson/elements/Text';
import SuccessOverlay from '@mirinae/lessons/components/lesson/elements/shared/SuccessOverlay';

const Container = styled.div`
    flex-direction: column;
    position: relative;
    display: flex;
    align-items: center;
`;

const Sentence = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${lessonsPalette.grey[4]};
    border-radius: 13px;
    padding: 27px 55px;
    margin-bottom: 25px;
    align-items: center;
    justify-content: center;
    width: fit-content;
  
    ${os.isOthers ? `
        padding: 27px 55px;
        margin-bottom: 25px;
    ` : `
        padding: 15px 15px;
        margin-bottom: 25px;
    `}
`;

const SentenceSlots = styled.div`
    word-wrap: break-word;
    word-break: keep-all;
    margin-bottom: 11px;
    display: block;
`;

const SentenceSlot = styled.div`
    font-family: ${fontkoKR};
    font-size: 24px;
    font-weight: bold;
    white-space: pre;
    display: inline-block;
    color: ${lessonsPalette.darken[1]};
    // margin: ${props => (props.frag.top === ' ' ? '6px' : 'reset')};
`;

const EmptySlot = styled.div`
    width: ${props => props.lineWidth}px;
    border-bottom: 2px solid ${lessonsPalette.grey[4]};
    display: inline-block;
    margin: 2px;
    font-family: 'Noto Sans KR',sans-serif;
`;

const SentenceEnglish = styled.div`
    color: ${lessonsPalette.grey[2]};
    font-size: 16px;
`;

const GoalSentenceEnglish = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 720px;
    height: 50px;
    margin-bottom: 30px;
`;

const AnswerLine = styled.div`
    display: flex;
    justify-content: center;
    min-width: 520px;
    height: 41px;
    border-bottom: solid 2px #d7d7d7;
    padding-bottom: 45px;
    margin-bottom: 20px;
`;

const ChoiceWords = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    visibility: ${props => (props.show ? 'visible' : 'hidden')};
`;

const ChoiceWord = styled.div`
    padding: 18px;
    border: 1px solid ${lessonsPalette.grey[5]};
    box-shadow: 3px 3px 7px rgba(159, 188, 186, 0.4);
    border-radius: 10px;
    font-size: 25px;
    font-weight: 600;
    color: ${lessonsPalette.grey[0]};
    cursor: pointer;
    animation: ${props => (props.wiggle ? 'wiggle 0.25s' : '')};
    visibility: ${props => (props.complete ? 'hidden' : props.show ? 'visible' : 'hidden')};
    white-space: nowrap;
    background-color: ${lessonsPalette.primary.white};
    ${props => props.stagger && '&:nth-child(2n) { transform: translateY(45%); }'};

    &:hover {
        background-color: ${lessonsPalette.lessons[4]};
        border-color: ${lessonsPalette.darken[0]};
        box-shadow: none;
    }

    ${os.isOthers ? `
        margin: 15px;
    ` : `
        margin: 10px;
    `}

    @keyframes wiggle {
        0% { transform: rotate(0deg); }
        5% { transform: rotate(-5deg); }
       80% { transform: rotate(5deg); }
      100% { transform: rotate(0deg); }
    }
`;

const BadChoiceWord = styled(ChoiceWord)`
    border-color: ${lessonsPalette.lessons[2]};
    background-color: ${lessonsPalette.lessons[3]};
    box-shadow: unset;
    &:hover {
        background-color: ${lessonsPalette.lessons[3]};
        border-color: ${lessonsPalette.lessons[2]};
        box-shadow: none;
    }
`;

const SentenceBuilder = ({ element }) => {
    const player = useContext(PlayerContext);
    const {
        style,
        sentenceToComplete,
        wordsToPick,
    } = getFields(
        element,
        'style',
        'sentenceToComplete',
        'wordsToPick',
    );

    // the 'picks' constitute the syllable tiles that a user can select to fill
    // in a blank. this set comprises both the 'otherPicks' field and all of
    // the fragment slots from sentenceToComplete that are marked as hidden.
    // update:
    //   the full set of pickable words is now in wordsToPick, shuffled here to randomize ordering each play
    //   the sentenceToComplete still contains the words needed, hidden or not, so those words
    //   have to be supplied twice, in sentenceToComplete and wordsToPick.  This was done to make in-editor
    //   display easier to update
    const picks = useMemo(() => shuffle(wordsToPick.words), []); // shuffle(wordsToPick), []);
    const [sentence, setSentence] = useState(sentenceToComplete.words);
    const [complete, setComplete] = useState(false);
    const [badPick, setBadPick] = useState(null);
    const [pickIndices, setPickIndices] = useState([]);
    const textRef = useRef(null);
    const englishSentence = element.fieldsByName.englishSentence;

    const checkForSuccess = () => {
        if (!complete && !find(sentence, (frag) => frag.hidden)) {
            setComplete(true);
            player.postEvent({ action: 'complete' }, element);
            GTM.userAction('success', { page: player.currentPage.componentName }, 'lessons');
        }
    };

    const onClick = (chars, i) => {
        setBadPick(null);
        const nextFragIndex = findIndex(sentence, (frag) => frag.hidden && !frag.picked);

        if (chars === sentence[nextFragIndex].top) {
            sentence[nextFragIndex].hidden = false;
            setSentence(sentence.concat());
            setPickIndices(pickIndices.concat([i]));
        } else {
            setBadPick(i);
            GTM.userAction('error', { page: player.currentPage.componentName }, 'lessons');
            player.postEvent({ action: 'error' }, element);
       }

        checkForSuccess();
    };

    const gatherText = (words) => words.map(w => (w.color === 'space' ? ' ' : w.top)).join('');

    return (
        <Element element={element}>
            <Container>
                { style === 'Complete the phrase' && (
                    <Sentence>
                        { complete ? <SuccessOverlay top={-55} /> : null }
                        <SentenceSlots complete={complete} ref={textRef}>
                            { sentence.map((frag, i) => (
                                frag.hidden ? [...frag.top].map((c, j) => <EmptySlot key={j} lineWidth={18} />)
                                    : <SentenceSlot key={i} complete={complete} frag={frag} dangerouslySetInnerHTML={{ __html: frag.top }} />
                            ))}
                        </SentenceSlots>
                        <SentenceEnglish>{englishSentence.value}</SentenceEnglish>
                    </Sentence>
                )}
                { style === 'Unscramble' && (
                    <>
                        <GoalSentenceEnglish className="goal-text-element">
                            <FormattedText text={englishSentence.value} styles={englishSentence.styles} />
                        </GoalSentenceEnglish>
                        <AnswerLine>
                            { complete ? <SuccessOverlay top={-55} /> : null }
                            <SentenceSlots ref={textRef}>
                                { sentence.map((frag, i) => (
                                    !frag.hidden && <SentenceSlot key={i} complete={complete} frag={frag} dangerouslySetInnerHTML={{ __html: frag.top }} />
                                ))}
                            </SentenceSlots>
                        </AnswerLine>
                    </>
                )}
                { style === 'Goal check' && (
                    <>
                        <GoalSentenceEnglish className="goal-text-element">
                            <FormattedText text={englishSentence.value} styles={englishSentence.styles} />
                        </GoalSentenceEnglish>
                        <AnswerLine>
                            { complete ? <SuccessOverlay top={-55} /> : null }
                            <SentenceSlots ref={textRef}>
                                { sentence.map((frag, i) => (
                                    !frag.hidden && <SentenceSlot key={i} complete={complete} frag={frag} dangerouslySetInnerHTML={{ __html: frag.top }} />
                                ))}
                            </SentenceSlots>
                        </AnswerLine>
                    </>
                )}
                <ChoiceWords>
                    { picks.map((frag, i) => {
                        const isBadPick = badPick === i;
                        const WordType = isBadPick ? BadChoiceWord : ChoiceWord;
                        return (
                            <WordType
                                complete={complete} show={!pickIndices.includes(i)} wiggle={isBadPick} key={i} stagger={picks.length > 2}
                                onClick={() => onClick(frag.top, i)}
                            >
                                {frag.top}
                            </WordType>
                        );
                    })}
                </ChoiceWords>
            </Container>
        </Element>
    );
};

export default SentenceBuilder;
